// The résumé download, presented as a build artifact.
//
// Two controls, one behaviour:
//   * an ARTIFACTS line under the run stats, where a CI run would list what it
//     produced — this is the primary, and it's above the fold;
//   * a glass pill that docks to the bottom of the viewport once that line
//     scrolls away, so the action is still reachable deep in the page.
//
// The pill is driven by an IntersectionObserver on the line rather than a
// scroll offset, so it stays correct at any window size or content length.
//
// Everything visible is CMS-editable (see ResumeConfig): the file, the label,
// the action word, the section heading, and the icon. Only the *facts* —
// format and byte size — are derived, read from a HEAD request against the
// real file so they can't drift from what actually downloads.

import { escapeHtml } from './dom';
import { occurredAt } from './regression';
import type { Milestone } from './types';

export interface ResumeConfig {
  /** Where the file lives. Absent → no controls render at all. */
  url: string;
  /** Name the browser saves it as. */
  filename: string;
  /** The visible link text, e.g. "resume.pdf" or "Résumé (PDF)". */
  label: string;
  /** The call-to-action word on the pill, e.g. "get" or "download". */
  action: string;
  /** The eyebrow above the line, e.g. "artifacts" or "artifact". */
  section: string;
  /** A preset icon name (see ICONS) or any literal glyph — an emoji works. */
  icon: string;
}

const DEFAULTS = {
  filename: 'resume.pdf',
  action: 'get',
  section: 'artifacts',
  icon: 'download',
} as const;

const STEP_MS = 260;
const DONE_HOLD_MS = 2600;
const PILL_DONE_HOLD_MS = 2000;

/**
 * Preset icons, drawn in the same 24×24 stroke style as src/icons.ts so they
 * sit with the rest of the UI. Anything not named here is treated as a literal
 * glyph, which is how emoji support falls out for free.
 */
const ICONS: Record<string, string> = {
  download: `<path d="M12 3v12"/><path d="m7 11 5 5 5-5"/><path d="M4 20h16"/>`,
  file: `<path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8Z"/><path d="M14 3v5h5"/>`,
  terminal: `<path d="m5 8 4 4-4 4"/><path d="M13 16h6"/>`,
  package: `<path d="m12 3 8 4.5v9L12 21l-8-4.5v-9L12 3Z"/><path d="M4 7.5 12 12l8-4.5"/><path d="M12 12v9"/>`,
  disk: `<path d="M5 3h11l3 3v15H5Z"/><path d="M8 3v6h8V3"/><path d="M8 21v-7h8v7"/>`,
  check: `<path d="m4 12 5 5L20 6"/>`,
};

/** Preset name → inline SVG; anything else → the glyph itself, escaped. */
function renderIcon(icon: string): string {
  const preset = ICONS[icon];
  if (preset) {
    return `<svg class="rl-artifact-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${preset}</svg>`;
  }
  // Cap by code point, not by char — an emoji is often a surrogate pair, and
  // slicing mid-pair renders a replacement box.
  const glyph = [...icon].slice(0, 2).join('');
  return glyph
    ? `<span class="rl-artifact-glyph" aria-hidden="true">${escapeHtml(glyph)}</span>`
    : '';
}

export function resumeConfigFrom(settings: Record<string, string>): ResumeConfig | null {
  const url = settings['resume.url']?.trim();
  if (!url) return null;
  const filename = settings['resume.filename']?.trim() || DEFAULTS.filename;
  return {
    url,
    filename,
    // The filename is the sensible default label — it's already the most
    // informative string available, and it's what the download will be called.
    label: settings['resume.label']?.trim() || filename,
    action: settings['resume.action']?.trim() || DEFAULTS.action,
    section: settings['resume.section']?.trim() || DEFAULTS.section,
    icon: settings['resume.icon']?.trim() || DEFAULTS.icon,
  };
}

/* ------------------------------------------------------------ file facts -- */

function formatBytes(bytes: number): string {
  if (bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const TYPE_LABELS: Record<string, string> = {
  'application/pdf': 'PDF',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
};

function typeFromFilename(filename: string): string {
  const ext = filename.split('.').pop();
  return ext && ext !== filename ? ext.toUpperCase() : '';
}

/**
 * Real format and size, via HEAD so nothing is downloaded to display them.
 *
 * Best-effort by design: a HEAD can be refused, or the size hidden behind a
 * proxy. The meta line is an enhancement, so failure means showing less rather
 * than showing something invented.
 */
async function probeFile(config: ResumeConfig): Promise<string> {
  const fallback = typeFromFilename(config.filename);
  try {
    const response = await fetch(config.url, { method: 'HEAD' });
    if (!response.ok) return fallback;
    const type = response.headers.get('content-type')?.split(';')[0]?.trim() ?? '';
    const length = Number(response.headers.get('content-length') ?? '');
    const label = TYPE_LABELS[type] ?? fallback;
    const size = Number.isFinite(length) ? formatBytes(length) : '';
    return [label, size].filter(Boolean).join(' · ');
  } catch {
    return fallback;
  }
}

/* ------------------------------------------------------------- the build -- */

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Earliest to latest year across the run, e.g. "2014–2026". */
function yearSpan(milestones: Milestone[]): string {
  const years = milestones
    .map(occurredAt)
    .filter((iso): iso is string => iso !== null)
    .map((iso) => Number(iso.slice(0, 4)))
    .filter((year) => Number.isFinite(year));
  if (years.length === 0) return '—';
  const min = Math.min(...years);
  const max = Math.max(...years);
  return min === max ? `${min}` : `${min}–${max}`;
}

/**
 * Hands the blob to the browser as a download.
 *
 * Fetched rather than linked because `<a download>` is ignored cross-origin —
 * the file lives on the CMS host, so a plain link would navigate to it (or open
 * the PDF in a tab) instead of saving it. If the fetch is blocked we fall back
 * to opening it, which at least gets the visitor to the file.
 */
async function saveFile(config: ResumeConfig): Promise<number> {
  const response = await fetch(config.url);
  if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
  const blob = await response.blob();

  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = config.filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoke late — revoking immediately can cancel the download in Safari.
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
  return blob.size;
}

/* ---------------------------------------------------------- artifact line -- */

/** The inline ARTIFACTS row. Rendered as a string so it fits renderOutput. */
export function renderArtifactsLine(config: ResumeConfig | null): string {
  if (!config) return '';
  return `
    <div class="rl-artifacts" data-artifacts>
      <span class="rl-artifacts-section">${escapeHtml(config.section)}</span>
      <button class="rl-artifact-link" type="button" data-resume-run>
        ${renderIcon(config.icon)}<span>${escapeHtml(config.label)}</span>
      </button>
      <span class="rl-artifact-meta" data-artifact-meta></span>
    </div>
  `;
}

/* ------------------------------------------------------------------ pill -- */

function buildPill(config: ResumeConfig): HTMLDivElement {
  const pill = document.createElement('div');
  pill.className = 'rl-pill';
  pill.innerHTML = `
    <button class="rl-pill-btn" type="button" data-resume-run>
      ${renderIcon(config.icon)}
      <span class="rl-pill-label">${escapeHtml(config.label)}</span>
      <span class="rl-pill-meta" data-artifact-meta></span>
      <span class="rl-pill-go">${escapeHtml(config.action)}</span>
    </button>
  `;
  return pill;
}

/* ----------------------------------------------------------------- mount -- */

interface Controller {
  /** Re-attach to a freshly painted artifacts line. */
  observe: (root: HTMLElement) => void;
  destroy: () => void;
}

/**
 * Wires both controls. Call once; call `observe` after each repaint, since
 * innerHTML replaces the artifacts line the observer was watching.
 */
export function mountResume(config: ResumeConfig, milestones: Milestone[]): Controller {
  const pill = buildPill(config);
  document.body.appendChild(pill);

  // Fill both meta slots once, from one HEAD request.
  let meta = '';
  void probeFile(config).then((value) => {
    meta = value;
    document.querySelectorAll<HTMLElement>('[data-artifact-meta]').forEach((node) => {
      node.textContent = value;
    });
  });

  let observer: IntersectionObserver | null = null;
  let running = false;

  function paintMeta(): void {
    if (!meta) return;
    document.querySelectorAll<HTMLElement>('[data-artifact-meta]').forEach((node) => {
      node.textContent = meta;
    });
  }

  /** The floating build log, anchored under whichever control was clicked. */
  function logFor(trigger: HTMLElement): HTMLDivElement {
    const existing = document.querySelector<HTMLDivElement>('.rl-buildlog');
    existing?.remove();
    const log = document.createElement('div');
    log.className = 'rl-buildlog';
    log.setAttribute('role', 'status');
    const anchor = trigger.closest('.rl-artifacts') ?? trigger.closest('.rl-pill');
    (anchor ?? document.body).appendChild(log);
    return log;
  }

  async function run(trigger: HTMLElement): Promise<void> {
    if (running) return;
    running = true;

    const isPill = !!trigger.closest('.rl-pill');
    const pillLabel = pill.querySelector<HTMLElement>('.rl-pill-go');
    const started = performance.now();
    const log = isPill ? null : logFor(trigger);

    if (pillLabel && isPill) pillLabel.textContent = 'building…';

    let size = 0;
    let failure: string | null = null;
    const work = saveFile(config)
      .then((bytes) => {
        size = bytes;
      })
      .catch((error: unknown) => {
        failure = error instanceof Error ? error.message : 'download failed';
      });

    if (log) {
      const line = (html: string): void => {
        const row = document.createElement('div');
        row.className = 'rl-buildlog-line';
        row.innerHTML = html;
        log.appendChild(row);
      };

      const steps: { label: string; value: () => string }[] = [
        { label: 'collecting cases', value: () => `${milestones.length} ok` },
        { label: 'resolving dates', value: () => yearSpan(milestones) },
        { label: 'formatting', value: () => 'ats profile' },
        { label: `writing ${config.filename}`, value: () => formatBytes(size) || '—' },
      ];

      line(`<span class="rl-prompt">$</span> <span class="rl-cmd">make resume</span>`);
      const pace = prefersReducedMotion() ? 0 : STEP_MS;
      for (const step of steps) {
        // The last step reports the real byte count, so it waits for the fetch
        // rather than racing it.
        if (step === steps[steps.length - 1]) await work;
        else await sleep(pace);
        if (failure) break;
        line(
          `<span class="rl-buildlog-tick">▸</span> ${escapeHtml(step.label)} <span class="rl-buildlog-value">${escapeHtml(step.value())}</span>`,
        );
      }
    }

    await work;

    if (failure) {
      // Most likely CORS. Opening still gets the visitor to the file.
      window.open(config.url, '_blank', 'noopener');
      log?.appendChild(
        Object.assign(document.createElement('div'), {
          className: 'rl-buildlog-line',
          innerHTML: `<span class="rl-buildlog-warn">! opened in a new tab instead</span>`,
        }),
      );
      if (pillLabel) pillLabel.textContent = config.action;
    } else if (log) {
      const elapsed = ((performance.now() - started) / 1000).toFixed(1);
      const row = document.createElement('div');
      row.className = 'rl-buildlog-line';
      row.innerHTML = `<span class="rl-buildlog-ok">✓ done in ${elapsed}s</span>`;
      log.appendChild(row);
    } else if (pillLabel) {
      pillLabel.textContent = '✓ saved';
      window.setTimeout(() => {
        pillLabel.textContent = config.action;
      }, PILL_DONE_HOLD_MS);
    }

    running = false;
    if (log && !prefersReducedMotion()) window.setTimeout(() => log.remove(), DONE_HOLD_MS);
  }

  document.addEventListener('click', (event) => {
    const trigger = (event.target as HTMLElement).closest<HTMLElement>('[data-resume-run]');
    if (!trigger) return;
    void run(trigger);
  });

  return {
    observe(root: HTMLElement): void {
      observer?.disconnect();
      const line = root.querySelector('[data-artifacts]');
      paintMeta();
      if (!line) {
        // No line on screen (empty filter result) — don't strand the pill.
        pill.classList.remove('is-visible');
        return;
      }
      observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            pill.classList.toggle('is-visible', !entry.isIntersecting);
          }
        },
        { threshold: 0 },
      );
      observer.observe(line);
    },
    destroy(): void {
      observer?.disconnect();
      pill.remove();
    },
  };
}
